import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { useFrame } from '@react-three/fiber';
import { Suspense, memo, useEffect, useMemo, useRef } from 'react';
import * as THREE from 'three';
import { resolveCameraProximityOpacity, resolveCutawayYaw, resolveCylinderCameraProximity, resolveSphereCameraProximity } from '../_pure/cameraProximity.js';
import { advanceSectorSliceCompression } from '../_pure/sectorSliceSurface.js';
import { areRenderPropsValueEqual } from '../_pure/valueEquality.js';
import { useGpuResources } from '../useGpuResources.js';
import { ItemLabel } from './ItemLabel.js';
import { XR_TRAVEL_SURFACE } from '../_pure/xrTravel.js';
import { SilhouetteContour } from './SilhouetteContour.js';
/** Owns a private material so its per-frame opacity never leaks into items sharing the cached one. */
function CameraProximityFadedMesh(props) {
    const { meshProps, geometry, material, fade, frame, volume } = props;
    const meshRef = useRef(null);
    const eye = useMemo(() => new THREE.Vector3(), []);
    const fadedMaterial = useMemo(() => {
        const clone = material.clone();
        clone.transparent = true;
        clone.depthWrite = false;
        return clone;
    }, [material]);
    useEffect(() => () => fadedMaterial.dispose(), [fadedMaterial]);
    useFrame(({ camera }) => {
        const mesh = meshRef.current;
        if (!mesh)
            return;
        camera.getWorldPosition(eye);
        let proximity;
        if (volume) {
            if (!frame.current)
                return;
            frame.current.updateWorldMatrix(true, false);
            frame.current.worldToLocal(eye);
            proximity = resolveCylinderCameraProximity({ cameraLocal: [eye.x, eye.y, eye.z], ...volume });
        }
        else {
            if (geometry.boundingSphere === null)
                geometry.computeBoundingSphere();
            mesh.updateWorldMatrix(true, false);
            const sphere = geometry.boundingSphere.clone().applyMatrix4(mesh.matrixWorld);
            proximity = resolveSphereCameraProximity({
                camera: [eye.x, eye.y, eye.z], center: [sphere.center.x, sphere.center.y, sphere.center.z], radius: sphere.radius,
            });
        }
        fadedMaterial.opacity = material.opacity * resolveCameraProximityOpacity({
            proximity, fade,
        });
    });
    return _jsx("mesh", { ref: meshRef, ...meshProps, userData: XR_TRAVEL_SURFACE, geometry: geometry, material: fadedMaterial, dispose: null });
}
function CameraFacingGroup(props) {
    const ref = useRef(null);
    const eye = useMemo(() => new THREE.Vector3(), []);
    useFrame(({ camera }) => {
        if (!props.frame.current || !ref.current)
            return;
        camera.getWorldPosition(eye);
        props.frame.current.updateWorldMatrix(true, false);
        props.frame.current.worldToLocal(eye);
        ref.current.rotation.y = resolveCutawayYaw([eye.x, eye.y, eye.z]);
    });
    return _jsx("group", { ref: ref, children: props.children });
}
function NormalCompressedGroup(props) {
    const ref = useRef(null);
    useFrame((_, delta) => {
        const group = ref.current;
        if (!group || group.scale.y === props.normalScale)
            return;
        group.scale.y = advanceSectorSliceCompression(group.scale.y, props.normalScale, delta);
    });
    return _jsx("group", { ref: ref, children: props.children });
}
/** Renders resolved surfaces and capabilities without knowledge of shape or layout kinds. */
function EntityItemRendererImpl(props) {
    const { plan, surfaces, outlines, meshProps, labels, labelProximity, } = props;
    const frame = useRef(null);
    useGpuResources(props.resourceCache, [...surfaces, ...outlines].flatMap(surface => [surface.geometry, surface.material]));
    const interactionProps = plan.interaction === 'pass-through' ? { raycast: () => null }
        : plan.interaction === 'item' ? meshProps : {};
    const labelContent = _jsx(Suspense, { fallback: null, children: labels.map((label, index) => (_jsx(ItemLabel, { plan: label, proximity: labelProximity, cameraProximityFade: plan.cameraProximityFade }, index))) });
    const content = (_jsxs(_Fragment, { children: [surfaces.map(surface => plan.cameraProximityFade === undefined
                ? _jsx("mesh", { ...interactionProps, geometry: surface.geometry, material: surface.material, userData: XR_TRAVEL_SURFACE, ...(surface.renderOrder === undefined ? {} : { renderOrder: surface.renderOrder }), dispose: null }, surface.key)
                : (_jsx(CameraProximityFadedMesh, { meshProps: { ...interactionProps,
                        ...(surface.renderOrder === undefined ? {} : { renderOrder: surface.renderOrder }) }, geometry: surface.geometry, material: surface.material, fade: plan.cameraProximityFade, frame: frame, volume: plan.proximityVolume }, surface.key))), plan.labelFrame === 'placement' ? null : labelContent, outlines.map(outline => _jsx("lineSegments", { geometry: outline.geometry, material: outline.material, dispose: null }, outline.key)), plan.silhouetteContour ? _jsx(SilhouetteContour, { contour: plan.silhouetteContour }) : null] }));
    const compressed = plan.normalScale === undefined ? content
        : _jsx(NormalCompressedGroup, { normalScale: plan.normalScale, children: content });
    const facing = plan.cameraFacingCutaway ? _jsx(CameraFacingGroup, { frame: frame, children: compressed }) : compressed;
    const surfaceContent = _jsx("group", { ref: frame, ...(plan.localPosition === undefined ? {} : { position: plan.localPosition }), ...(plan.rotation === undefined ? {} : { rotation: plan.rotation }), ...(plan.quaternion === undefined ? {} : { quaternion: plan.quaternion }), children: plan.scale === undefined ? facing : _jsx("group", { scale: plan.scale, children: facing }) });
    return (_jsx("group", { position: plan.position, children: plan.labelFrame === 'placement' ? _jsxs(_Fragment, { children: [surfaceContent, labelContent] }) : surfaceContent }));
}
/** Skips re-rendering when an item's resolved props are unchanged by value; playback re-resolves every item each frame. */
export const EntityItemRenderer = memo(EntityItemRendererImpl, areRenderPropsValueEqual);
